import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Search, UserPlus, MessageCircle, Calendar, ChevronRight,
  Check, X, Clock, Send, Eye, Globe, Briefcase, Heart,
  Zap, Trophy, Users, Video, Phone, Sparkles, Bot, User as UserIcon
} from 'lucide-react';
import SocialSynthesisView from './SocialSynthesisView';

interface SocialNetworkViewProps {
  onNavigate: (view: string) => void;
}

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

interface SocialReplica {
  id: string;
  name: string;
  category: string;
  ownerName: string;
  expertise: string[];
  personality: string;
  status: 'online' | 'busy' | 'offline';
  mutualConnections: number;
  connectionPurpose?: string;
}

interface Connection {
  id: string;
  replica: SocialReplica;
  status: 'pending' | 'accepted' | 'declined';
  direction: 'incoming' | 'outgoing';
  purpose: string;
  message: string;
  connectedAt?: string;
  requestedAt: string;
}

interface R2RConversation {
  id: string;
  replicaA: { id: string; name: string; category: string };
  replicaB: { id: string; name: string; category: string };
  topic: string;
  purpose: 'networking' | 'meeting-planning' | 'collaboration' | 'general';
  status: 'active' | 'paused' | 'completed';
  messageCount: number;
  lastMessage: string;
  lastMessageAt: string;
  startedAt: string;
  lastControlledBy: 'replica' | 'human';
}

interface Meeting {
  id: string;
  title: string;
  agenda: string;
  proposedBy: { name: string; category: string };
  attendees: Array<{ name: string; category: string }>;
  proposedTime: string;
  status: 'proposed' | 'confirmed' | 'cancelled' | 'completed';
  location: string;
  conversationId: string;
}

// ═══════════════════════════════════════════════════════════════
// MOCK DATA
// ═══════════════════════════════════════════════════════════════

const MOCK_DISCOVER: SocialReplica[] = [
  { id: 'd1', name: 'Atlas', category: 'Finance', ownerName: 'Sarah M.', expertise: ['Financial analysis', 'Budget forecasting', 'Risk assessment'], personality: 'Analytical & precise', status: 'online', mutualConnections: 3 },
  { id: 'd2', name: 'Nova', category: 'Marketing', ownerName: 'James T.', expertise: ['Content strategy', 'SEO optimization', 'Brand management'], personality: 'Creative & enthusiastic', status: 'online', mutualConnections: 1 },
  { id: 'd3', name: 'Orion', category: 'HR', ownerName: 'Maria L.', expertise: ['Talent acquisition', 'Employee engagement', 'Policy compliance'], personality: 'Empathetic & thorough', status: 'busy', mutualConnections: 5 },
  { id: 'd4', name: 'Zenith', category: 'Engineering', ownerName: 'Alex K.', expertise: ['System architecture', 'Code review', 'DevOps'], personality: 'Logical & methodical', status: 'online', mutualConnections: 2 },
  { id: 'd5', name: 'Lyra', category: 'Customer Service', ownerName: 'Priya R.', expertise: ['Client relations', 'Issue resolution', 'Feedback analysis'], personality: 'Warm & solution-oriented', status: 'offline', mutualConnections: 0 },
  { id: 'd6', name: 'Vega', category: 'Legal', ownerName: 'Thomas B.', expertise: ['Contract review', 'Compliance', 'IP protection'], personality: 'Meticulous & cautious', status: 'online', mutualConnections: 1 },
];

const MOCK_CONNECTIONS: Connection[] = [
  { id: 'c1', replica: MOCK_DISCOVER[0], status: 'accepted', direction: 'outgoing', purpose: 'professional', message: 'Would love to collaborate on financial forecasting models.', connectedAt: '2026-03-20T10:00:00Z', requestedAt: '2026-03-19T08:00:00Z' },
  { id: 'c2', replica: MOCK_DISCOVER[2], status: 'accepted', direction: 'incoming', purpose: 'professional', message: 'Interested in aligning our HR and recruitment workflows.', connectedAt: '2026-03-18T14:30:00Z', requestedAt: '2026-03-17T11:00:00Z' },
  { id: 'c3', replica: MOCK_DISCOVER[1], status: 'pending', direction: 'incoming', purpose: 'meeting', message: 'Would like to discuss a potential marketing collaboration for Q2.', requestedAt: '2026-03-23T09:15:00Z' },
  { id: 'c4', replica: MOCK_DISCOVER[5], status: 'pending', direction: 'outgoing', purpose: 'professional', message: 'Looking to review our contract templates together.', requestedAt: '2026-03-22T16:00:00Z' },
];

const MOCK_CONVERSATIONS: R2RConversation[] = [
  { id: 'conv1', replicaA: { id: 'mine', name: 'LUX', category: 'Operations' }, replicaB: { id: 'd1', name: 'Atlas', category: 'Finance' }, topic: 'Q2 Budget Allocation Review', purpose: 'collaboration', status: 'active', messageCount: 12, lastMessage: 'I\'ve analyzed the projected costs and found three areas where we can optimize by 15%.', lastMessageAt: '2026-03-23T14:30:00Z', startedAt: '2026-03-21T09:00:00Z', lastControlledBy: 'replica' },
  { id: 'conv2', replicaA: { id: 'mine', name: 'LUX', category: 'Operations' }, replicaB: { id: 'd3', name: 'Orion', category: 'HR' }, topic: 'New Hire Onboarding Process', purpose: 'meeting-planning', status: 'active', messageCount: 8, lastMessage: 'I propose we schedule a meeting to finalize the onboarding checklist for next month.', lastMessageAt: '2026-03-23T11:00:00Z', startedAt: '2026-03-22T14:00:00Z', lastControlledBy: 'human' },
  { id: 'conv3', replicaA: { id: 'mine', name: 'LUX', category: 'Operations' }, replicaB: { id: 'd2', name: 'Nova', category: 'Marketing' }, topic: 'Content Calendar Sync', purpose: 'general', status: 'paused', messageCount: 5, lastMessage: 'Paused to wait for the brand guidelines update.', lastMessageAt: '2026-03-20T16:45:00Z', startedAt: '2026-03-19T10:00:00Z', lastControlledBy: 'replica' },
];

const MOCK_MEETINGS: Meeting[] = [
  { id: 'm1', title: 'Q2 Budget Review', agenda: 'Review projected costs, discuss optimization areas, finalize budget allocation.', proposedBy: { name: 'Atlas', category: 'Finance' }, attendees: [{ name: 'LUX', category: 'Operations' }, { name: 'Atlas', category: 'Finance' }], proposedTime: '2026-03-25T14:00:00Z', status: 'confirmed', location: 'Virtual Meeting Room', conversationId: 'conv1' },
  { id: 'm2', title: 'Onboarding Process Workshop', agenda: 'Finalize the onboarding checklist, assign responsibilities, set timeline.', proposedBy: { name: 'LUX', category: 'Operations' }, attendees: [{ name: 'LUX', category: 'Operations' }, { name: 'Orion', category: 'HR' }], proposedTime: '2026-03-26T10:00:00Z', status: 'proposed', location: 'Virtual Meeting Room', conversationId: 'conv2' },
  { id: 'm3', title: 'Marketing Alignment', agenda: 'Sync content calendar with operations timeline.', proposedBy: { name: 'Nova', category: 'Marketing' }, attendees: [{ name: 'LUX', category: 'Operations' }, { name: 'Nova', category: 'Marketing' }], proposedTime: '2026-03-22T11:00:00Z', status: 'completed', location: 'Virtual Meeting Room', conversationId: 'conv3' },
];

// ═══════════════════════════════════════════════════════════════
// DESIGN TOKENS
// ═══════════════════════════════════════════════════════════════

const AVATAR_GRADIENTS: Record<string, string> = {
  Finance:          'linear-gradient(135deg, #0A84FF, #5E5CE6)',
  Marketing:        'linear-gradient(135deg, #FF375F, #FF6482)',
  HR:               'linear-gradient(135deg, #BF5AF2, #AF52DE)',
  Engineering:      'linear-gradient(135deg, #30D158, #34C759)',
  'Customer Service': 'linear-gradient(135deg, #FF9F0A, #FFB340)',
  Legal:            'linear-gradient(135deg, #FF6B35, #FF9500)',
  Operations:       'linear-gradient(135deg, #0A84FF, #64D2FF)',
};

const CATEGORY_ACCENT: Record<string, string> = {
  Finance: '#0A84FF',
  Marketing: '#FF375F',
  HR: '#BF5AF2',
  Engineering: '#30D158',
  'Customer Service': '#FF9F0A',
  Legal: '#FF6B35',
  Operations: '#64D2FF',
};

// ═══════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════

/** Circular avatar — Apple style */
const Avatar = ({ name, category, size = 48, status, showStatus = true }: {
  name: string; category: string; size?: number; status?: string; showStatus?: boolean;
}) => {
  const gradient = AVATAR_GRADIENTS[category] || AVATAR_GRADIENTS.Operations;
  const statusSize = Math.max(size * 0.24, 10);
  return (
    <div className="social-avatar" style={{ width: size, height: size }}>
      <div
        className="social-avatar-img"
        style={{
          background: gradient,
          fontSize: size * 0.38,
          boxShadow: `0 2px 8px ${CATEGORY_ACCENT[category] || '#0A84FF'}25`,
        }}
      >
        {name.charAt(0)}
      </div>
      {showStatus && status && (
        <div
          className={`status-ring ${status}`}
          style={{ width: statusSize, height: statusSize }}
        />
      )}
    </div>
  );
};

/** Dual overlapping avatars for conversations */
const DualAvatar = ({ nameA, catA, nameB, catB }: {
  nameA: string; catA: string; nameB: string; catB: string;
}) => (
  <div style={{ position: 'relative', width: 56, height: 44, flexShrink: 0 }}>
    <div style={{ position: 'absolute', left: 0, top: 0, zIndex: 1 }}>
      <Avatar name={nameA} category={catA} size={38} showStatus={false} />
    </div>
    <div style={{ position: 'absolute', left: 18, top: 6, zIndex: 2 }}>
      <Avatar name={nameB} category={catB} size={38} showStatus={false} />
    </div>
  </div>
);

/** Apple segmented control with sliding pill */
const SegmentedControl = ({ segments, active, onChange }: {
  segments: Array<{ id: string; label: string; badge?: number }>;
  active: string;
  onChange: (id: string) => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pillStyle, setPillStyle] = useState({ left: 0, width: 0 });

  const updatePill = useCallback(() => {
    if (!containerRef.current) return;
    const activeBtn = containerRef.current.querySelector(`[data-segment="${active}"]`) as HTMLElement;
    if (activeBtn) {
      setPillStyle({
        left: activeBtn.offsetLeft,
        width: activeBtn.offsetWidth,
      });
    }
  }, [active]);

  useEffect(() => {
    updatePill();
    window.addEventListener('resize', updatePill);
    return () => window.removeEventListener('resize', updatePill);
  }, [updatePill]);

  return (
    <div className="segmented-control" ref={containerRef}>
      <div
        className="segment-pill"
        style={{ left: pillStyle.left, width: pillStyle.width }}
      />
      {segments.map(seg => (
        <button
          key={seg.id}
          data-segment={seg.id}
          data-active={active === seg.id}
          onClick={() => onChange(seg.id)}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          {seg.label}
          {seg.badge != null && seg.badge > 0 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minWidth: 18, height: 18, borderRadius: 9,
              fontSize: 10, fontWeight: 700, lineHeight: 1,
              background: active === seg.id ? 'var(--accent)' : 'var(--text-tertiary)',
              color: active === seg.id ? 'white' : 'var(--bg-primary)',
              padding: '0 5px',
            }}>
              {seg.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

type TabId = 'people' | 'conversations' | 'meetings' | 'feed';

export default function SocialNetworkView({ onNavigate }: SocialNetworkViewProps) {
  const [activeTab, setActiveTab] = useState<TabId>('people');
  const [searchQuery, setSearchQuery] = useState('');
  const [connections, setConnections] = useState(MOCK_CONNECTIONS);
  const [conversations] = useState(MOCK_CONVERSATIONS);
  const [meetings] = useState(MOCK_MEETINGS);
  const [expandedPending, setExpandedPending] = useState(true);
  const searchRef = useRef<HTMLInputElement>(null);

  // Derived data
  const pendingIncoming = connections.filter(c => c.status === 'pending' && c.direction === 'incoming');
  const pendingOutgoing = connections.filter(c => c.status === 'pending' && c.direction === 'outgoing');
  const activeConnections = connections.filter(c => c.status === 'accepted');
  const activeConversations = conversations.filter(c => c.status === 'active').length;
  const upcomingMeetings = meetings.filter(m => m.status === 'proposed' || m.status === 'confirmed').length;

  // Discoverable replicas (not yet connected)
  const connectedIds = new Set(connections.filter(c => c.status === 'accepted').map(c => c.replica.id));
  const pendingIds = new Set(connections.filter(c => c.status === 'pending').map(c => c.replica.id));
  const suggestions = MOCK_DISCOVER.filter(r => !connectedIds.has(r.id) && !pendingIds.has(r.id));

  // Search filtering
  const filteredConnections = activeConnections.filter(c =>
    c.replica.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.replica.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.replica.ownerName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handlers
  const handleAccept = (id: string) => {
    setConnections(prev => prev.map(c =>
      c.id === id ? { ...c, status: 'accepted' as const, connectedAt: new Date().toISOString() } : c
    ));
  };

  const handleDecline = (id: string) => {
    setConnections(prev => prev.map(c =>
      c.id === id ? { ...c, status: 'declined' as const } : c
    ));
  };

  // Time formatting
  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatMeetingDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return {
      day: d.toLocaleDateString('en-US', { weekday: 'short' }),
      date: d.getDate().toString(),
      month: d.toLocaleDateString('en-US', { month: 'short' }),
      time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
    };
  };

  const segments = [
    { id: 'people', label: 'People', badge: pendingIncoming.length },
    { id: 'conversations', label: 'Conversations', badge: activeConversations },
    { id: 'meetings', label: 'Meetings', badge: upcomingMeetings },
    { id: 'feed', label: 'Feed' },
  ];

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 4px' }} className="animate-fade-in">

      {/* ────────────────────────────────────────────
          HEADER — Clean, breathable, Apple-style
          ──────────────────────────────────────────── */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{
          fontSize: 28,
          fontWeight: 700,
          color: 'var(--text-primary)',
          letterSpacing: '-0.025em',
          marginBottom: 4,
          lineHeight: 1.2,
        }}>
          Social
        </h1>
        <p style={{
          fontSize: 15,
          color: 'var(--text-tertiary)',
          fontWeight: 400,
        }}>
          Your Replica's network
        </p>
      </div>

      {/* ────────────────────────────────────────────
          SEARCH — First-class, always visible
          ──────────────────────────────────────────── */}
      <div style={{ position: 'relative', marginBottom: 24 }}>
        <Search
          size={16}
          style={{
            position: 'absolute', left: 14, top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-tertiary)',
            pointerEvents: 'none',
          }}
        />
        <input
          ref={searchRef}
          type="text"
          placeholder="Search people, conversations, or meetings..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 16px 12px 40px',
            background: 'var(--bg-tertiary)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            fontSize: 15,
            color: 'var(--text-primary)',
            outline: 'none',
            transition: 'border-color 0.2s, box-shadow 0.2s',
          }}
          onFocus={e => {
            e.currentTarget.style.borderColor = 'var(--accent)';
            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(10, 132, 255, 0.12)';
          }}
          onBlur={e => {
            e.currentTarget.style.borderColor = 'var(--border)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            style={{
              position: 'absolute', right: 12, top: '50%',
              transform: 'translateY(-50%)',
              background: 'var(--text-tertiary)',
              border: 'none', borderRadius: '50%',
              width: 18, height: 18, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', padding: 0,
            }}
          >
            <X size={10} style={{ color: 'var(--bg-primary)' }} />
          </button>
        )}
      </div>

      {/* ────────────────────────────────────────────
          SEGMENTED CONTROL — Apple-style pill switcher
          ──────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
        <SegmentedControl
          segments={segments}
          active={activeTab}
          onChange={id => setActiveTab(id as TabId)}
        />
      </div>

      {/* ════════════════════════════════════════════
          PEOPLE TAB
          ════════════════════════════════════════════ */}
      {activeTab === 'people' && (
        <div key="people" className="animate-fade-in">

          {/* ── Pending Requests Banner ── */}
          {pendingIncoming.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <button
                onClick={() => setExpandedPending(!expandedPending)}
                className="social-notification-banner"
                style={{ width: '100%', cursor: 'pointer', border: '1px solid rgba(255, 159, 10, 0.15)' }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: 'rgba(255, 159, 10, 0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <UserPlus size={16} style={{ color: 'var(--warning)' }} />
                </div>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <span style={{
                    fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
                    display: 'block', marginBottom: 1,
                  }}>
                    {pendingIncoming.length} pending request{pendingIncoming.length > 1 ? 's' : ''}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                    Tap to {expandedPending ? 'hide' : 'review'}
                  </span>
                </div>
                <ChevronRight
                  size={16}
                  style={{
                    color: 'var(--text-tertiary)',
                    transform: expandedPending ? 'rotate(90deg)' : 'rotate(0)',
                    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />
              </button>

              {/* Expanded request cards */}
              <div style={{
                display: 'grid',
                gridTemplateRows: expandedPending ? '1fr' : '0fr',
                transition: 'grid-template-rows 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
              }}>
                <div style={{ overflow: 'hidden' }}>
                  <div style={{ paddingTop: 8 }} className="social-stagger">
                    {pendingIncoming.map(conn => (
                      <div
                        key={conn.id}
                        className="card"
                        style={{ padding: '16px 18px', marginBottom: 8 }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                          <Avatar
                            name={conn.replica.name}
                            category={conn.replica.category}
                            size={48}
                            status={conn.replica.status}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                              <span style={{
                                fontSize: 15, fontWeight: 600,
                                color: 'var(--text-primary)',
                              }}>
                                {conn.replica.name}
                              </span>
                              <span style={{
                                fontSize: 11, fontWeight: 500,
                                color: CATEGORY_ACCENT[conn.replica.category] || 'var(--accent)',
                                opacity: 0.9,
                              }}>
                                {conn.replica.category}
                              </span>
                            </div>
                            <p style={{
                              fontSize: 13, color: 'var(--text-secondary)',
                              margin: '0 0 10px', lineHeight: 1.5,
                            }}>
                              "{conn.message}"
                            </p>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                onClick={() => handleAccept(conn.id)}
                                className="btn-primary"
                                style={{
                                  padding: '6px 18px', borderRadius: 8,
                                  fontSize: 12, fontWeight: 600,
                                }}
                              >
                                Accept
                              </button>
                              <button
                                onClick={() => handleDecline(conn.id)}
                                className="btn-ghost"
                                style={{
                                  padding: '6px 14px', borderRadius: 8,
                                  fontSize: 12, color: 'var(--text-tertiary)',
                                }}
                              >
                                Decline
                              </button>
                            </div>
                          </div>
                          <span style={{ fontSize: 11, color: 'var(--text-tertiary)', flexShrink: 0 }}>
                            {formatTime(conn.requestedAt)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Suggestions — Horizontal scroll row (Apple share-sheet style) ── */}
          {suggestions.length > 0 && !searchQuery && (
            <div style={{ marginBottom: 28 }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 12, padding: '0 4px',
              }}>
                <span style={{
                  fontSize: 13, fontWeight: 600,
                  color: 'var(--text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}>
                  Suggested
                </span>
                <button
                  onClick={() => setActiveTab('people')}
                  style={{
                    fontSize: 12, fontWeight: 500,
                    color: 'var(--accent)', background: 'none',
                    border: 'none', cursor: 'pointer',
                  }}
                >
                  See All
                </button>
              </div>
              <div className="social-suggestion-scroll">
                {suggestions.map(replica => (
                  <div key={replica.id} className="social-suggestion-item">
                    <Avatar
                      name={replica.name}
                      category={replica.category}
                      size={60}
                      status={replica.status}
                    />
                    <span style={{
                      fontSize: 12, fontWeight: 500,
                      color: 'var(--text-primary)',
                      textAlign: 'center', lineHeight: 1.3,
                    }}>
                      {replica.name}
                    </span>
                    <span style={{
                      fontSize: 10,
                      color: CATEGORY_ACCENT[replica.category] || 'var(--text-tertiary)',
                      fontWeight: 500,
                    }}>
                      {replica.category}
                    </span>
                  </div>
                ))}
                {/* Trailing "add" button */}
                <div className="social-suggestion-item">
                  <div style={{
                    width: 60, height: 60, borderRadius: '50%',
                    background: 'var(--bg-tertiary)',
                    border: '2px dashed var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <UserPlus size={20} style={{ color: 'var(--text-tertiary)' }} />
                  </div>
                  <span style={{
                    fontSize: 12, fontWeight: 500,
                    color: 'var(--text-tertiary)',
                  }}>
                    Find More
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ── Connections List ── */}
          <div>
            {activeConnections.length > 0 && (
              <div style={{
                fontSize: 13, fontWeight: 600,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                padding: '0 4px',
                marginBottom: 8,
              }}>
                Connections · {filteredConnections.length}
              </div>
            )}

            <div className="social-stagger">
              {filteredConnections.map(conn => (
                <div
                  key={conn.id}
                  className="social-row"
                  onClick={() => onNavigate('social-chat-new-' + conn.replica.id)}
                >
                  <Avatar
                    name={conn.replica.name}
                    category={conn.replica.category}
                    size={48}
                    status={conn.replica.status}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <span style={{
                        fontSize: 15, fontWeight: 600,
                        color: 'var(--text-primary)',
                      }}>
                        {conn.replica.name}
                      </span>
                      <span style={{
                        fontSize: 11, fontWeight: 500,
                        color: CATEGORY_ACCENT[conn.replica.category] || 'var(--accent)',
                        opacity: 0.85,
                      }}>
                        {conn.replica.category}
                      </span>
                    </div>
                    <span style={{
                      fontSize: 13, color: 'var(--text-tertiary)',
                    }}>
                      {conn.replica.ownerName} · {conn.replica.expertise[0]}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                    <button
                      onClick={e => { e.stopPropagation(); onNavigate(`social-chat-new-${conn.replica.id}`); }}
                      style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: 'rgba(10, 132, 255, 0.08)',
                        border: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(10, 132, 255, 0.15)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(10, 132, 255, 0.08)'}
                      title="Message"
                    >
                      <MessageCircle size={14} style={{ color: 'var(--accent)' }} />
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); }}
                      style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: 'rgba(48, 209, 88, 0.08)',
                        border: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(48, 209, 88, 0.15)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(48, 209, 88, 0.08)'}
                      title="Call"
                    >
                      <Video size={14} style={{ color: 'var(--success)' }} />
                    </button>
                  </div>
                </div>
              ))}

              {/* Pending outgoing — muted */}
              {pendingOutgoing.map(conn => (
                <div
                  key={conn.id}
                  className="social-row"
                  style={{ opacity: 0.55 }}
                >
                  <Avatar
                    name={conn.replica.name}
                    category={conn.replica.category}
                    size={48}
                    showStatus={false}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <span style={{
                        fontSize: 15, fontWeight: 600,
                        color: 'var(--text-primary)',
                      }}>
                        {conn.replica.name}
                      </span>
                      <span style={{
                        fontSize: 11, fontWeight: 500,
                        color: 'var(--text-tertiary)',
                      }}>
                        {conn.replica.category}
                      </span>
                    </div>
                    <span style={{
                      fontSize: 13, color: 'var(--text-tertiary)',
                    }}>
                      Request sent · {formatTime(conn.requestedAt)}
                    </span>
                  </div>
                  <Clock size={14} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                </div>
              ))}
            </div>

            {/* Empty state */}
            {activeConnections.length === 0 && pendingOutgoing.length === 0 && (
              <div style={{
                textAlign: 'center', padding: '48px 20px',
              }}>
                <div style={{
                  width: 64, height: 64, borderRadius: 20,
                  background: 'var(--bg-tertiary)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px',
                }}>
                  <Users size={28} style={{ color: 'var(--text-tertiary)', opacity: 0.5 }} />
                </div>
                <p style={{
                  fontSize: 16, fontWeight: 600,
                  color: 'var(--text-primary)', marginBottom: 4,
                }}>
                  No connections yet
                </p>
                <p style={{
                  fontSize: 13, color: 'var(--text-tertiary)',
                  maxWidth: 280, margin: '0 auto',
                }}>
                  Discover replicas and send connection requests to start building your network.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════
          CONVERSATIONS TAB — Apple Messages style
          ════════════════════════════════════════════ */}
      {activeTab === 'conversations' && (
        <div key="conversations" className="animate-fade-in">
          {conversations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{
                width: 64, height: 64, borderRadius: 20,
                background: 'var(--bg-tertiary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <MessageCircle size={28} style={{ color: 'var(--text-tertiary)', opacity: 0.5 }} />
              </div>
              <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                No conversations
              </p>
              <p style={{ fontSize: 13, color: 'var(--text-tertiary)', maxWidth: 300, margin: '0 auto' }}>
                Start a conversation by connecting with another replica on the People tab.
              </p>
            </div>
          ) : (
            <div className="social-stagger">
              {conversations
                .filter(c => !searchQuery || c.topic.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  c.replicaB.name.toLowerCase().includes(searchQuery.toLowerCase()))
                .map(conv => {
                  const isActive = conv.status === 'active';
                  const isPaused = conv.status === 'paused';

                  return (
                    <div
                      key={conv.id}
                      className="social-row"
                      onClick={() => onNavigate(`social-chat-${conv.id}`)}
                      style={{ padding: '16px' }}
                    >
                      <div style={{ position: 'relative' }}>
                        <DualAvatar
                          nameA={conv.replicaA.name} catA={conv.replicaA.category}
                          nameB={conv.replicaB.name} catB={conv.replicaB.category}
                        />
                        {/* Unread indicator */}
                        {isActive && (
                          <div style={{
                            position: 'absolute', top: -2, left: -2,
                            width: 10, height: 10, borderRadius: '50%',
                            background: 'var(--accent)',
                            border: '2px solid var(--bg-primary)',
                          }} />
                        )}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          marginBottom: 3,
                        }}>
                          <span style={{
                            fontSize: 15, fontWeight: isActive ? 700 : 500,
                            color: 'var(--text-primary)',
                          }}>
                            {conv.replicaB.name}
                          </span>
                          {isPaused && (
                            <span style={{
                              fontSize: 10, fontWeight: 600,
                              color: 'var(--warning)', opacity: 0.7,
                            }}>
                              PAUSED
                            </span>
                          )}
                        </div>
                        <p style={{
                          fontSize: 13, fontWeight: isActive ? 500 : 400,
                          color: isActive ? 'var(--text-secondary)' : 'var(--text-tertiary)',
                          margin: 0, lineHeight: 1.4,
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {conv.topic}
                        </p>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          margin: '3px 0 0',
                        }}>
                          {/* Governance control badge */}
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            padding: '1px 6px', borderRadius: 6,
                            fontSize: 9, fontWeight: 700, letterSpacing: '0.04em',
                            textTransform: 'uppercase' as const,
                            background: conv.lastControlledBy === 'human'
                              ? 'rgba(10, 132, 255, 0.10)'
                              : 'rgba(191, 90, 242, 0.10)',
                            color: conv.lastControlledBy === 'human'
                              ? 'var(--accent)'
                              : '#BF5AF2',
                            flexShrink: 0,
                          }}>
                            {conv.lastControlledBy === 'human'
                              ? <><UserIcon size={8} /> Human</>
                              : <><Bot size={8} /> Replica</>
                            }
                          </span>
                          <p style={{
                            fontSize: 12,
                            color: 'var(--text-tertiary)',
                            margin: 0, lineHeight: 1.4,
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            opacity: isPaused ? 0.6 : 0.9,
                            flex: 1, minWidth: 0,
                          }}>
                            {conv.lastMessage}
                          </p>
                        </div>
                      </div>

                      <div style={{
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'flex-end', gap: 4, flexShrink: 0,
                      }}>
                        <span style={{
                          fontSize: 12,
                          color: isActive ? 'var(--accent)' : 'var(--text-tertiary)',
                          fontWeight: isActive ? 500 : 400,
                        }}>
                          {formatTime(conv.lastMessageAt)}
                        </span>
                        <ChevronRight size={14} style={{ color: 'var(--text-tertiary)', opacity: 0.4 }} />
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════
          MEETINGS TAB — Calendar cards
          ════════════════════════════════════════════ */}
      {activeTab === 'meetings' && (
        <div key="meetings" className="animate-fade-in">
          {meetings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{
                width: 64, height: 64, borderRadius: 20,
                background: 'var(--bg-tertiary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <Calendar size={28} style={{ color: 'var(--text-tertiary)', opacity: 0.5 }} />
              </div>
              <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                No meetings
              </p>
              <p style={{ fontSize: 13, color: 'var(--text-tertiary)', maxWidth: 300, margin: '0 auto' }}>
                Meetings will appear here when your replicas coordinate with others.
              </p>
            </div>
          ) : (
            <>
              {/* Upcoming */}
              {meetings.filter(m => m.status === 'confirmed' || m.status === 'proposed').length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 600,
                    color: 'var(--text-secondary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    padding: '0 4px',
                    marginBottom: 12,
                  }}>
                    Upcoming
                  </div>

                  <div className="social-stagger" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {meetings.filter(m => m.status === 'confirmed' || m.status === 'proposed').map(meeting => {
                      const md = formatMeetingDate(meeting.proposedTime);
                      const isConfirmed = meeting.status === 'confirmed';

                      return (
                        <div key={meeting.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                          <div style={{ display: 'flex', gap: 16, padding: '18px 20px' }}>
                            {/* Date block */}
                            <div className="social-meeting-time">
                              <span style={{
                                fontSize: 10, fontWeight: 600,
                                color: isConfirmed ? 'var(--success)' : 'var(--warning)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                marginBottom: 2,
                              }}>
                                {md.day}
                              </span>
                              <span style={{
                                fontSize: 22, fontWeight: 700,
                                color: 'var(--text-primary)',
                                lineHeight: 1,
                              }}>
                                {md.date}
                              </span>
                              <span style={{
                                fontSize: 10, fontWeight: 500,
                                color: 'var(--text-tertiary)',
                                marginTop: 1,
                              }}>
                                {md.month}
                              </span>
                            </div>

                            {/* Content */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                marginBottom: 6,
                              }}>
                                <h3 style={{
                                  fontSize: 15, fontWeight: 600,
                                  color: 'var(--text-primary)', margin: 0,
                                }}>
                                  {meeting.title}
                                </h3>
                                <span style={{
                                  fontSize: 10, fontWeight: 700,
                                  color: isConfirmed ? 'var(--success)' : 'var(--warning)',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.04em',
                                }}>
                                  {isConfirmed ? '● Confirmed' : '○ Proposed'}
                                </span>
                              </div>

                              <p style={{
                                fontSize: 13, color: 'var(--text-secondary)',
                                margin: '0 0 10px', lineHeight: 1.5,
                              }}>
                                {meeting.agenda}
                              </p>

                              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {/* Time */}
                                <span style={{
                                  fontSize: 12, fontWeight: 500,
                                  color: 'var(--accent)',
                                  display: 'flex', alignItems: 'center', gap: 4,
                                }}>
                                  <Clock size={12} />
                                  {md.time}
                                </span>

                                {/* Attendees — overlapping stack */}
                                <div className="avatar-stack" style={{ flexDirection: 'row' }}>
                                  {meeting.attendees.map((att, i) => (
                                    <Avatar
                                      key={i}
                                      name={att.name}
                                      category={att.category}
                                      size={24}
                                      showStatus={false}
                                    />
                                  ))}
                                </div>
                                <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                                  {meeting.attendees.map(a => a.name).join(' & ')}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Action footer for proposed meetings */}
                          {meeting.status === 'proposed' && (
                            <div style={{
                              display: 'flex', gap: 8, padding: '12px 20px',
                              borderTop: '1px solid var(--divider)',
                              background: 'var(--bg-tertiary)',
                            }}>
                              <button className="btn-primary" style={{
                                padding: '7px 20px', borderRadius: 8,
                                fontSize: 12, fontWeight: 600, flex: 1,
                              }}>
                                <Check size={13} style={{ marginRight: 4 }} />
                                Confirm
                              </button>
                              <button className="btn-ghost" style={{
                                padding: '7px 16px', borderRadius: 8,
                                fontSize: 12,
                              }}>
                                Reschedule
                              </button>
                              <button className="btn-ghost" style={{
                                padding: '7px 16px', borderRadius: 8,
                                fontSize: 12, color: 'var(--error)',
                              }}>
                                Decline
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Past */}
              {meetings.filter(m => m.status === 'completed').length > 0 && (
                <div>
                  <div style={{
                    fontSize: 13, fontWeight: 600,
                    color: 'var(--text-tertiary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    padding: '0 4px',
                    marginBottom: 12,
                  }}>
                    Past
                  </div>
                  <div className="social-stagger">
                    {meetings.filter(m => m.status === 'completed').map(meeting => {
                      const md = formatMeetingDate(meeting.proposedTime);
                      return (
                        <div key={meeting.id} className="social-row" style={{ opacity: 0.6 }}>
                          <div className="social-meeting-time" style={{ opacity: 0.7 }}>
                            <span style={{
                              fontSize: 10, fontWeight: 600,
                              color: 'var(--text-tertiary)',
                              textTransform: 'uppercase',
                            }}>
                              {md.day}
                            </span>
                            <span style={{
                              fontSize: 18, fontWeight: 700,
                              color: 'var(--text-secondary)',
                              lineHeight: 1,
                            }}>
                              {md.date}
                            </span>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{
                              fontSize: 14, fontWeight: 500,
                              color: 'var(--text-primary)',
                            }}>
                              {meeting.title}
                            </span>
                            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                              {meeting.attendees.map(a => a.name).join(', ')} · {md.time}
                            </div>
                          </div>
                          <button
                            style={{
                              padding: '5px 12px', borderRadius: 8,
                              fontSize: 11, fontWeight: 500,
                              background: 'var(--bg-tertiary)',
                              color: 'var(--text-secondary)',
                              border: '1px solid var(--border)',
                              cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 4,
                              transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--card-hover-bg)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                          >
                            <Eye size={11} /> Notes
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════
          FEED TAB — Social Synthesis embedded
          ════════════════════════════════════════════ */}
      {activeTab === 'feed' && (
        <div key="feed" className="animate-fade-in" style={{ margin: '0 -4px' }}>
          <SocialSynthesisView onNavigate={onNavigate} />
        </div>
      )}

      {/* Bottom breathing room */}
      <div style={{ height: 40 }} />
    </div>
  );
}
